import React, { ReactNode, useState, useMemo, useEffect } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { ScaleFade } from '@fiverr-private/transition';
import { AppI18n } from '../../i18n';
import { BriefFloatingFormPopup, BriefFloatingFormPopupProps } from '../../components';
import { TriggerScenario } from '../../components/BriefFloatingFormPopup/types';
import {
  reportClickedBriefFloatingFormButtonEvent,
  reportViewedBriefFloatingFormPopupEvent,
  reportViewedBriefFloatingFormButtonEvent,
} from '../../monitoring';
import { useGetInitialPrompt } from '../../hooks';
import { BffIcon } from './BffIcon';
import { ListingFlow } from './types';

import { bffTrigger, fixedContainer } from './style.ve.css';

export { ListingFlow, TriggerScenario };

// 23px, 92px -> Making sure the position of the trigger is after the global chat
const FixedContainer = ({ children }: { children: ReactNode }) => (
  <Container position="fixed" bottom="23px" right="92px" className={fixedContainer}>
    {children}
  </Container>
);

export interface BriefFloatingFormTriggerProps
  extends Pick<BriefFloatingFormPopupProps, 'triggerScenario' | 'pageName' | 'currency' | 'onEditBriefClick'> {
  listingFlow: ListingFlow;
  leafCategory?: number;
  searchQuery?: string;
  dlc?: number;
  forceOpen?: boolean;
  afterForceOpen?: () => void;
  afterForceOpenRefused?: () => void;
  onClose?: () => void;
  openingPosition?: number;
}

export const BriefFloatingFormTrigger = ({
  triggerScenario,
  currency,
  pageName,
  listingFlow,
  leafCategory,
  searchQuery,
  dlc,
  forceOpen = false,
  afterForceOpen,
  afterForceOpenRefused,
  onClose,
  openingPosition,
  onEditBriefClick,
}: React.PropsWithChildren<BriefFloatingFormTriggerProps>) => {
  const [isReady, setIsReady] = useState(false);
  const [showTrigger, setShowTrigger] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [lockedTriggerScenario, setLockedTriggerScenario] = useState<TriggerScenario | undefined>(undefined);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsReady(true);
    }, 2000);

    return () => clearTimeout(timeout);
  }, []);

  const handleOpen = () => {
    setIsPopupOpen(true);
    setHasOpened(true);

    reportViewedBriefFloatingFormPopupEvent({
      pageName,
      openingPosition,
      initialPromptText: initialPromptAndProfession?.initialPrompt || '',
      triggerScenario,
    });
  };

  useEffect(() => {
    if (forceOpen) {
      if (!hasOpened) {
        setLockedTriggerScenario((prev) => prev ?? triggerScenario);

        handleOpen();

        afterForceOpen?.();
      } else {
        afterForceOpenRefused?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  const handleManualOpening = () => {
    reportClickedBriefFloatingFormButtonEvent(pageName);

    handleOpen();
  };

  const queryParams = useMemo(() => {
    if (listingFlow === 'business_categories') {
      return { leafCategory };
    } else if (listingFlow === 'business_search') {
      return { searchQuery, leafCategory: dlc };
    }
    return {};
  }, [listingFlow, leafCategory, searchQuery, dlc]);

  const { data: initialPromptAndProfession } = useGetInitialPrompt(queryParams);

  useEffect(() => {
    if (showTrigger && initialPromptAndProfession) {
      reportViewedBriefFloatingFormButtonEvent(pageName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPromptAndProfession, pageName]);

  if (!initialPromptAndProfession || !isReady) return null;

  return (
    <>
      {/* Popup */}
      <FixedContainer>
        <ScaleFade
          in={isPopupOpen}
          easing={{ enter: 'easeIn', exit: 'easeOut' }}
          duration={{ enter: 'slow1', exit: 'moderate1' }}
          delay="moderate1"
          onAnimationStart={() => isPopupOpen && setShowTrigger(false)}
          onAnimationEnd={() => {
            if (!isPopupOpen) {
              setShowTrigger(true);
              setLockedTriggerScenario(undefined);
              onClose?.();
            }
          }}
        >
          <Container position="fixed" bottom="16px" right="92px" zIndex="overlay">
            <BriefFloatingFormPopup
              initialPromptAndProfession={initialPromptAndProfession}
              triggerScenario={lockedTriggerScenario}
              currency={currency}
              pageName={pageName}
              setIsPopupOpen={setIsPopupOpen}
              onEditBriefClick={onEditBriefClick}
            />
          </Container>
        </ScaleFade>
      </FixedContainer>

      {/* Trigger */}
      <FixedContainer>
        <ScaleFade in={showTrigger} easing={{ enter: 'easeIn' }} duration={{ enter: 'slow1' }}>
          <Container
            onClick={handleManualOpening}
            display="flex"
            paddingY="2"
            paddingX="4"
            gap="2"
            cursor="pointer"
            justifyContent="center"
            borderRadius="2xl"
            className={bffTrigger}
            alignItems="center"
            boxShadow="z3"
            maxHeight="46px"
            background="white"
          >
            <BffIcon />
            <Text fontWeight="semibold" size="b_sm">
              <AppI18n k="brief_floating_form.trigger.cta" />
            </Text>
          </Container>
        </ScaleFade>
      </FixedContainer>
    </>
  );
};
